import convertApprovalLine from './convertApprovalLine';

/**
 * 문서 정보를 API 요청 형식으로 변환하는 유틸 함수
 * - PC 버전과 동일한 필드 구조 적용
 * @param {Object} params - 문서 데이터 변환 파라미터
 * @param {number} params.mode - 저장 모드 (0: 상신, 1: 임시문서 상신, 2: 재상신, 3: 임시저장)
 * @param {number} params.companyCode - 회사 코드
 * @param {number} params.enumber - 사용자 번호
 * @param {number} params.docType - 문서 양식 코드
 * @param {string} params.title - 문서 제목
 * @param {string} params.contents - 문서 내용
 * @param {string} [params.contentsImg=null] - 문서 내용 이미지 (Base64 인코딩)
 * @param {number} [params.docState=2] - 문서 상태 (2: 상신)
 * @param {Object} params.approvalLine - 결재선 정보
 * @param {Array} params.recipients - 수신자 목록
 * @param {Array} params.executors - 시행자 목록
 * @param {Array} params.referenceDocuments - 참조문서 목록
 * @param {string} [params.summaryNote=''] - 요약 및 비고
 * @param {boolean} [params.useCorporateSeal=false] - 법인인감 사용 여부
 * @returns {Object} API 요청 형식의 문서 데이터
 */
function buildDocumentData({
  mode,
  companyCode,
  enumber,
  docType,
  title,
  contents,
  contentsImg = null,
  docState = 2,
  approvalLine,
  recipients,
  executors,
  referenceDocuments,
  attachments = [], // 첨부파일 목록 파라미터 추가
  summaryNote = '',
  useCorporateSeal = false,
  // [WEB 기획 제외] PC 버전에서는 보안등급/긴급여부 기능이 있으나, WEB 버전에서는 기획에서 제외됨
  // security = 3, // 보안등급 (1: 대외비, 2: 일반, 3: 공개)
  // hurry = 0,    // 긴급 여부 (0: 일반, 1: 긴급)
}) {
  // 결재선 정보 변환. mode 2(재상신)일 때는 기존 state 유지, mode 0/1일 때는 order 기준으로 state 결정.
  const approvalLineData = convertApprovalLine(approvalLine, enumber, docState, mode);

  // 수신자 목록 변환
  const senderList =
    recipients && recipients.length > 0
      ? recipients.map((recipient) => {
          if (typeof recipient === 'string') {
            return { name: recipient, code: 0 };
          }
          return { name: recipient.name || '', code: recipient.enumber || recipient.code || 0 };
        })
      : [];

  // 시행자 목록 변환
  const executorList =
    executors && executors.length > 0
      ? executors.map((executor) => {
          if (typeof executor === 'string') {
            return { name: executor, code: 0 };
          }
          const enumber = executor.enumber || executor.code || 0;
          return { name: executor.name || '', code: enumber };
        })
      : [];

  // 참조문서 목록 변환
  // 다양한 필드명 지원: idx, docIdx, document_idx, id
  const referenceDocList =
    referenceDocuments && referenceDocuments.length > 0
      ? referenceDocuments
          .map((doc) => {
            if (typeof doc === 'object' && doc !== null) {
              // 다양한 필드명에서 idx 추출
              const docIdx = doc.idx || doc.docIdx || doc.document_idx || doc.id;
              return docIdx || 0;
            }
            return doc;
          })
          .filter((idx) => idx && idx !== 0) // 유효한 idx만 필터링
      : [];

  // 첨부파일 목록 변환 (기존 파일만)
  // file.idx가 있고 0이 아닌 경우만 필터링 (idx가 0이면 신규 파일이므로 제외)
  // 다양한 필드명 지원: idx, fileIdx, file_idx, id
  const fileList =
    attachments && attachments.length > 0
      ? attachments
          .filter((file) => {
            if (typeof file === 'object' && file !== null) {
              // 다양한 필드명에서 idx 추출
              const fileIdx = file.idx || file.fileIdx || file.file_idx || file.id;
              // file.idx가 있고 0이 아닌 기존 파일만 필터링
              // file.file 객체가 있는 경우는 신규 파일이므로 제외
              return fileIdx && fileIdx !== 0 && !file.file;
            }
            return false;
          })
          .map((file) => {
            // 다양한 필드명에서 idx 추출
            return file.idx || file.fileIdx || file.file_idx || file.id || 0;
          })
          .filter((idx) => idx && idx !== 0) // 유효한 idx만 필터링
      : [];

  // 디버깅: 첨부파일 정보 확인
  if (attachments && attachments.length > 0) {
    console.log('[buildDocumentData] 첨부파일 정보:', {
      total: attachments.length,
      attachments: attachments.map((f) => ({
        idx: f.idx || f.fileIdx || f.file_idx || f.id,
        name: f.name || f.fileName,
        hasFile: !!f.file,
        raw: f, // 원본 데이터 확인용
      })),
      fileList,
      mode,
    });
  }

  // 디버깅: 참조문서 정보 확인
  if (referenceDocuments && referenceDocuments.length > 0) {
    console.log('[buildDocumentData] 참조문서 정보:', {
      total: referenceDocuments.length,
      referenceDocuments: referenceDocuments.map((doc) => ({
        idx: doc.idx || doc.docIdx || doc.document_idx || doc.id,
        title: doc.title || doc.doc_title || doc.name,
        raw: doc, // 원본 데이터 확인용
      })),
      referenceDocList,
      mode,
    });
  }

  // API 요청 데이터 구성
  const requestData = {
    mode,
    company: companyCode,
    enumber,
    docType,
    title,
    contents,
    // [WEB 기획 제외] PC 버전에서는 보안등급/긴급여부 기능이 있으나, WEB 버전에서는 기획에서 제외됨
    // 기본값으로 고정: security=3(공개), hurry=0(일반)
    security: 3,
    hurry: 0,
    docState,
    ...approvalLineData,
    senderList,
    executorList,
    referenceDocList,
    file_list: fileList, // 첨부파일 목록 추가
    summaryNote: summaryNote || '', // 요약 및 비고 (빈 문자열이면 빈 문자열로 전송, 백엔드에서 NULL 처리)
    is_corp_seal_used: useCorporateSeal ? 1 : 0, // 법인인감 사용 여부 (0: 미사용, 1: 사용)
  };

  // 문서 내용 이미지가 있으면 추가 (Base64 인코딩된 PNG 이미지)
  if (contentsImg) {
    requestData.contents_img = contentsImg;
  }

  return requestData;
}

export default buildDocumentData;
